import React, { useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import styled from 'styled-components';
import { ReactComponent as Logo } from '../logo.svg';
import { useTheme } from '../context/ThemeContext';
import { Box, Container, Typography } from '@mui/material';
import { FaFacebook, FaInstagram, FaYoutube, FaTiktok } from 'react-icons/fa';
import { socialLinks } from '../config/socialLinks';
import ContactLink from './ContactLink';
import ThemeToggle from './ThemeToggle';

const ContactBar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1200;
  background-image: url('https://www.hawkinscarpet.com/wp-content/uploads/2024/02/team-cleaning-residential-carpet.jpg');
  background-size: cover;
  background-position: center;
  height: 40px;
  display: flex;
  align-items: center;
  color: white;
  background-color: ${props => props.theme.primary};
  font-size: 1rem;
  @media (max-width: 600px) {
    font-size: 0.85rem;
    height: 34px;
  }
`;

const ContactContent = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 2rem;
  white-space: nowrap;
  @media (max-width: 600px) {
    padding: 0 0.5rem;
    gap: 0.5rem;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  white-space: nowrap;
  @media (max-width: 600px) {
    gap: 0.7rem;
  }
`;

const SocialIcons = styled.div`
  display: flex;
  gap: 1rem;
  a {
    color: white;
    font-size: 1.2rem;
    transition: color 0.2s;
    &:hover {
      color: ${props => props.theme.primaryDark};
    }
    @media (max-width: 600px) {
      font-size: 1rem;
    }
  }
  @media (max-width: 600px) {
    gap: 0.5rem;
  }
`;

const Nav = styled.nav`
  position: sticky;
  top: 40px;
  z-index: 1100;
  background-color: ${props => props.theme.background};
  box-shadow: 0 2px 4px ${props => props.theme.shadow};
  padding: 0.25rem 0;
`;

const MenuItems = styled.ul`
    list-style-type: none;
    display: flex;
    gap: 2em;
    align-items: center;
    margin: 0;
    padding: 0;
    @media (max-width: 768px) {
        flex-direction: column;
        background-color: ${props => props.theme.background};
        position: absolute;
        right: 0;
        top: 100%;
        width: 100%;
        height: ${props => props.open ? 'auto' : '0'};
        overflow: hidden;
        transition: height 0.2s ease-in-out;
        box-shadow: 0 2px 4px ${props => props.theme.shadow};
    }
`;

const MenuItem = styled.li`
    font-size: 1.1em;
    font-weight: 500;
    text-decoration: none;
    transition: color 0.2s ease-in-out;

    a {
        color: ${props => props.theme.text};
        text-decoration: none;
        transition: color 0.2s ease-in-out;
        &:visited {
            color: ${props => props.theme.text};
        }
        &:hover {
            color: ${props => props.theme.primaryDark} !important;
            font-weight: 550;
        }
        &.active {
            color: ${props => props.theme.primary};
            font-weight: 600;
        }
    }
`;

const HamburgerIcon = styled.i.attrs(() => ({ className: "hamburger-icons" }))`
    cursor: pointer;
    display: none;
    @media (max-width: 768px) {
        display: block;
    }
`;

const StyledLogoLink = styled(Link)`
    text-decoration: none;
    cursor: pointer;
    
    &:hover {
        .logo-text {
            color: ${props => props.theme.primaryDark};
            font-weight: 550;
        }
    }
`;

const ThemeToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-left: 1rem;
  @media (max-width: 768px) {
    margin: 1rem 0;
  }
`;

function Navbar() {
    const [open, setOpen] = useState(false);
    const { currentTheme } = useTheme();

    return (
        <>
            <ContactBar theme={currentTheme}>
                <ContactContent>
                    <ContactInfo>
                        <ContactLink href="tel:2703150389" isBold>270-315-0389</ContactLink>
                        <ContactLink href="mailto:hawkinscarpet@gmail.com" isBold>hawkinscarpet@gmail.com</ContactLink>
                    </ContactInfo>
                    <SocialIcons>
                        <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer"><FaFacebook /></a>
                        <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer"><FaInstagram /></a>
                        <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer"><FaYoutube /></a>
                        <a href={socialLinks.tiktok} target="_blank" rel="noopener noreferrer"><FaTiktok /></a>
                    </SocialIcons>
                </ContactContent>
            </ContactBar>
            <Nav theme={currentTheme}>
                <Container maxWidth='0.95vw' disableGutters>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2 }}>
                        <StyledLogoLink to="/" theme={currentTheme}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                <Logo className="menu-logo" alt="HawkinsCarpetLogo" style={{ height: '50px', fill: currentTheme.primary }} />
                                <Typography 
                                    className="logo-text"
                                    variant="h6" 
                                    sx={{ 
                                        color: currentTheme.text,
                                        transition: 'all 0.2s ease-in-out',
                                    }}
                                >
                                    Hawkins Carpet Cleaning
                                </Typography>
                            </div>
                        </StyledLogoLink>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <MenuItems open={open} theme={currentTheme}>
                                <MenuItem theme={currentTheme}><NavLink to="/" activeclassname="active" onClick={() => setOpen(false)}>Home</NavLink></MenuItem>
                                <MenuItem theme={currentTheme}><NavLink to="/book-now" activeclassname="active" onClick={() => setOpen(false)}>Book Online</NavLink></MenuItem>
                                <MenuItem theme={currentTheme}><NavLink to="/services" activeclassname="active" onClick={() => setOpen(false)}>Services</NavLink></MenuItem>
                                <MenuItem theme={currentTheme}><NavLink to="/our-work" activeclassname="active" onClick={() => setOpen(false)}>Our Work</NavLink></MenuItem>
                                <MenuItem theme={currentTheme}><NavLink to="/faq" activeclassname="active" onClick={() => setOpen(false)}>FAQ</NavLink></MenuItem>
                                <MenuItem theme={currentTheme}><NavLink to="/about-us" activeclassname="active" onClick={() => setOpen(false)}>About Us</NavLink></MenuItem>
                                <ThemeToggleWrapper>
                                    <ThemeToggle />
                                </ThemeToggleWrapper>
                            </MenuItems>
                            <HamburgerIcon onClick={() => setOpen(!open)}>
                                <span className="material-icons">{open ? 'close' : 'menu'}</span>
                            </HamburgerIcon>
                        </Box>
                    </Box>
                </Container>
            </Nav>
        </>
    );
}

export default Navbar;
